import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    FormGroup,
    Label,
    Input,
    Alert,
    Table,
    Badge,
    Spinner,
    Container,
    Modal,
    ModalHeader,
    ModalBody
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaMoneyBillWave, FaFileInvoice, FaTruck, FaPlus, FaMinus, FaList, FaUser, FaCalendarAlt, FaEdit, FaTrash, FaShieldAlt, FaArrowUp, FaArrowDown, FaBalanceScale, FaArrowLeft } from 'react-icons/fa';
import { reglementActions } from '../../sagas/reglementSlice';
import { formatPrice, preventScroll } from '../../helpers/utility';
import Select from 'react-select';
import './ReglementForm.css'; // Assuming you have some custom styles
import { clientActions } from '../../sagas/clientSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { CBL_PAGE, PAY_PAGE, RETOUR_PAGE } from '../../routes/routeConstants';
import POSHeader from './POSHeader';

const ReglementForm = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { clients } = useSelector(state => state.clients);

    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedChantier, setSelectedChantier] = useState(null);
    const [typeReglement, setTypeReglement] = useState('AVANCE');

    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });
    const navigate = useNavigate();
    const location = useLocation();
    const reglement = location.state?.reglement || null;
    const canEdit = reglement && reglement.statut === 'EN_ATTENTE' || !reglement;
    // Validation schema
    const validationSchema = Yup.object().shape({
        clientId: Yup.number().required(t('reglement.clientRequired')),
        // montantRegle: Yup.number()
        //     .positive(t('reglement.montantPositive'))
        //     .required(t('reglement.montantRequired')),
        dateReglement: Yup.date().required(t('reglement.dateReglementRequired')),
        modeReglement: Yup.string().required(t('reglement.modeReglementRequired')),
        statut: Yup.string().required(t('reglement.statutRequired')),

    });

    const { businessName, email, address, telephone, fullName } = useSelector(state => state.auth);
    const [activeTab, setActiveTab] = useState("AVANCE");

    // Initial values
    const initialValues = {
        id: reglement?.id || null,
        reference: reglement?.reference || '',
        clientId: reglement?.clientId || '',
        numFactures: reglement?.numFactures || '',
        numBonLivraisons: reglement?.numBonLivraisons || '',
        typeReglement: reglement?.typeReglement || 'FACTURE',
        montantRegle: reglement?.montantRegle || 0,
        dateReglement: reglement?.dateReglement || new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        dateEncaissement: reglement?.dateEncaissement || '',
        statut: reglement?.statut || 'EN_ATTENTE',
        statutPrecedent: reglement?.statutPrecedent || null,
        commentaire: reglement?.commentaire || '',
        modeReglement: reglement?.modeReglement || 'ESPECES',
        referencePaiement: reglement?.referencePaiement || '',
        numeroCheque: reglement?.numeroCheque || '',
        banque: reglement?.banque || '',
        dateEcheance: reglement?.dateEcheance || '',
        motifAvance: reglement?.motifAvance || '',
        dateValiditeAvance: reglement?.dateValiditeAvance || '',
        commentaireAvance: reglement?.commentaireAvance || '',
    };

    const modeReglementOptions = [
        { value: 'ESPECES', label: t('reglement.especes') },
        { value: 'CHEQUE', label: t('reglement.cheque') },
        { value: 'VIREMENT', label: t('reglement.virement') },
        { value: 'CARTE_BANCAIRE', label: t('reglement.carteBancaire') },
    ];

    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers
    })) ?? [];

    // Create chantier options from selected client's chantiers
    const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
        value: chantier.id,
        label: `${chantier.code} - ${chantier.nom}`,
        ...chantier
    })) || [];

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    useEffect(() => {
        dispatch(clientActions.search({
            query: ""
        }));

    }, []);

    useEffect(() => {
        if (reglement) {
            // Set selected client
            const client = clients.find(c => c.id === reglement.clientId);
            if (client) {
                setSelectedClient({
                    value: client.id,
                    label: client.typeClient === 'PARTICULIER'
                        ? `${client.prenom} ${client.nom}`
                        : client.entreprise,
                    solde: client.solde,
                    totalDebit: client.totalDebit,
                    totalCredit: client.totalCredit,
                    chantiers: client.chantiers
                });

                // Set selected chantier if exists
                if (reglement.chantierId && client.chantiers) {
                    const chantier = client.chantiers.find(ch => ch.id === reglement.chantierId);
                    if (chantier) {
                        setSelectedChantier({
                            value: chantier.id,
                            label: `${chantier.code} - ${chantier.nom}`,
                            ...chantier
                        });
                    }
                }
            }
            setTypeReglement(reglement.typeReglement);
        }
    }, [reglement, clients]);


    // Handle form submission
    const handleSubmit = (values, { setSubmitting }) => {
        const payload = {
            data: {
                ...values,
                typeReglement: typeReglement,
                chantierId: selectedChantier ? selectedChantier.value : null,
                montantRegle: values.montantRegle,
                estAvance: true,
                dateValiditeAvance: values.dateValiditeAvance,
                commentaireAvance: values.commentaireAvance,
            },
            onSuccess: (data) => {
                Swal.fire({
                    icon: 'success',
                    title: t('common.success'),
                    text: reglement ? t('reglement.updateSuccess') :
                        typeReglement === 'AVANCE' ? t('reglement.avanceCreateSuccess') :
                            t('reglement.createSuccess'),
                });
                dispatch(reglementActions.avanceClient(filters));
                navigate(PAY_PAGE);
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('common.error'),
                    text: error.message || t('common.errorOccurred'),
                });
                setSubmitting(false);
            }
        };

        if (reglement) {
            dispatch(reglementActions.updateRequest({ ...payload, id: reglement.id }));
        } else {
            dispatch(reglementActions.createRequest(payload));
        }
    };




    return (
        <Container fluid>
            <POSHeader
                businessName={businessName || "MATJARPOS"}
                fullName={fullName}
                activeTab={activeTab}
                onChangeTab={(tab) => {
                    setActiveTab(tab);

                    if (tab === "VENTES") navigate(CBL_PAGE);
                    if (tab === "AVANCE") navigate(PAY_PAGE);
                    if (tab === "RETOURS") navigate(RETOUR_PAGE);
                }}
            />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur }) => (
                    <Form>
                        <Row>
                            {/* Section Client et Type */}
                            <Col md={12}>
                                <Card className="mb-3 shadow-sm">
                                    
                                    <CardBody>
                                        <Row className="g-3 align-items-center">
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label for="clientId" className="fw-bold text-secondary">
                                                        <FaUser className="me-1" />
                                                        {t('reglement.client')} *
                                                    </Label>
                                                    <Select
                                                        options={clientOptions}
                                                        value={selectedClient}
                                                        onChange={(option) => {
                                                            if (canEdit) {
                                                                setSelectedClient(option);
                                                                setSelectedChantier(null); // Reset chantier selection
                                                                setFieldValue('clientId', option?.value || '');
                                                                if (option) {
                                                                    setFilters(f => ({ ...f, clientId: option.value }));
                                                                }
                                                            }
                                                        }}
                                                        placeholder={t('reglement.selectClient')}
                                                        isClearable={!reglement}
                                                        isSearchable={!reglement}
                                                        isDisabled={!canEdit}
                                                        className={`rounded-3 border border-2 ${errors.clientId && touched.clientId ? 'is-invalid' : ''}`}
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        styles={{
                                                            menuPortal: base => ({ ...base, zIndex: 9999 })
                                                        }}
                                                    />
                                                    <ErrorMessage name="clientId" component="div" className="text-danger small mt-1" />
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label className="fw-bold text-secondary">
                                                        {t('reglement.chantier', 'Chantier')}
                                                    </Label>
                                                    <Select
                                                        options={chantierOptions}
                                                        value={chantierOptions.find(option => option.value === selectedChantier?.value) || null}
                                                        onChange={(option) => setSelectedChantier(option)}
                                                        placeholder={t('reglement.selectChantier', 'Sélectionner...')}
                                                        isClearable
                                                        isSearchable
                                                        className="rounded-3 border border-2"
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        styles={{
                                                            menuPortal: base => ({ ...base, zIndex: 9999 })
                                                        }}
                                                        isDisabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label for="dateReglement" className="fw-bold text-secondary">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateReglement')} *
                                                    </Label>
                                                    <Field
                                                        name="dateReglement"
                                                        type="date"
                                                        className={`form-control rounded-3 border border-2 ${errors.dateReglement && touched.dateReglement ? 'is-invalid' : ''}`}
                                                        disabled={!canEdit}
                                                    />
                                                    <ErrorMessage name="dateReglement" component="div" className="invalid-feedback" />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                        <Card>
                            <CardBody>
                                <Row>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label for="montantRegle">
                                                {t('reglement.montantRegle')} *
                                            </Label>
                                            <Field
                                                name="montantRegle"
                                                type="number"
                                                onFocus={(e) => {
                                                    e.target.select();
                                                    e.target.addEventListener('wheel', preventScroll, { passive: false });
                                                }}
                                                onBlur={(e) => {
                                                    e.target.removeEventListener('wheel', preventScroll);
                                                }}

                                                className={`form-control ${errors.montantRegle && touched.montantRegle ? 'is-invalid' : ''}`}
                                                placeholder="0.00"
                                                value={values.montantRegle}
                                                onChange={(e) => {
                                                    if (canEdit) {
                                                        setFieldValue('montantRegle', e.target.value);
                                                    }
                                                }}
                                                disabled={!canEdit}
                                            />
                                            <ErrorMessage name="montantRegle" component="div" className="invalid-feedback" />


                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label for="modeReglement">{t('reglement.modeReglement')} *</Label>
                                            <Field
                                                as="select"
                                                name="modeReglement"
                                                className={`form-control ${errors.modeReglement && touched.modeReglement ? 'is-invalid' : ''}`}
                                                disabled={!canEdit}
                                            >
                                                <option value="">{t('reglement.selectModeReglement')}</option>
                                                {modeReglementOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="modeReglement" component="div" className="invalid-feedback" />
                                        </FormGroup>
                                    </Col>
                                    {(values.modeReglement === 'CHEQUE') && (
                                        <>
                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="numeroCheque">{t('reglement.numeroCheque')}</Label>
                                                    <Field
                                                        name="numeroCheque"
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={
                                                            values.modeReglement === 'CHEQUE'
                                                        }
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="banque">{t('reglement.banque')}</Label>
                                                    <Input
                                                        type="select"
                                                        id="banque"
                                                        name="banque"
                                                        value={values.banque}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        invalid={touched.banque && errors.banque}
                                                        disabled={!canEdit}
                                                    >
                                                        <option value="">{t('payment.selectBank', 'Sélectionner une banque')}</option>
                                                        <option value="BMCE">BMCE</option>
                                                        <option value="ATTIJARI">ATTIJARI</option>
                                                        <option value="CIH">CIH</option>
                                                        <option value="CREDIT DU MAROC">CREDIT DU MAROC</option>
                                                        <option value="SG">SG</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="dateEcheance">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateEcheance')}
                                                    </Label>
                                                    <Field
                                                        name="dateEcheance"
                                                        type="date"
                                                        className="form-control"
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </>

                                    )}
                                    {(values.modeReglement === 'VIREMENT') && (
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label for="referencePaiement">{t('reglement.referencePaiement')}</Label>
                                                <Field
                                                    name="referencePaiement"
                                                    type="text"
                                                    className="form-control"
                                                    disabled={!canEdit}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>



                                <Row className="mt-3">
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="commentaire">{t('reglement.commentaire')}</Label>
                                            <Field
                                                as="textarea"
                                                name="commentaire"
                                                rows="3"
                                                className="form-control"
                                                placeholder={t('reglement.commentairePlaceholder')}
                                                disabled={!canEdit}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                        {/* Boutons d'action */}
                        <Card className="mt-1 shadow-sm">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex gap-2">
                                        <Button
                                            type="submit"
                                            color="primary"
                                            disabled={isSubmitting || !selectedClient || !canEdit}
                                        >
                                            {isSubmitting && <Spinner size="sm" className="me-2" />}
                                            <i className={`fas ${reglement ? 'fa-save' : 'fa-plus'} me-1`}></i>
                                            {reglement ? t('common.update') : t('common.create')}
                                        </Button>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

export default ReglementForm;

